import type { GenericIconType } from "@/shared/ui/actions/GenericIcon";

export const PROFILE_SHAPING_LABELS = {
  flow: "Shape career profile",
  onboardingPromise: "Profile yourself from real experience",
  studioSituationCta: "Shape this situation",
  situation: "Situation",
  careerSituation: "Career situation",
  addSituation: "Add situation",
  addCareerSituation: "Add career situation",
  editSituation: "Edit situation",
  chooseSituation: "Choose situation",
  anchorToSituation: "Anchor to situation",
  reviewNeedsAnchor: "Resolve missing anchors",
  needsAnchor: "Needs anchor",
  anchorAndConfirm: "Anchor and confirm",
  allThoughtsAnchored: "All thoughts are anchored",
  backlogSourceCta: "Shape from source",
  originCreationPhase: "Store source material",
  originCreationAndExtractionAction: "Add and shape",
  addSourceMaterial: "Add source material",
  extractionPhase: "Shape from source",
  findTopics: "Find topics",
  manualPhase: "Add manually",
  reviewPhase: "Review suggestions",
  confirmationAction: "Confirm thoughts",
  profileFocus: "Focus lens",
  addFocus: "Add focus lens",
  editFocus: "Edit focus lens",
  chooseFocus: "Choose focus lens",
  tailorToFocus: "Tailor to focus",
  orderByFocus: "Order by focus lens",
} as const;

export const PROFILE_SHAPING_MESSAGES = {
  shapeProfile:
    "Use Backlog to keep source material, then manage the career profile in Studio.",
  shapeFromSource:
    "Shape this material into suggested thoughts and decide what belongs in your profile.",
  shapeThisSituation:
    "Add or review thoughts anchored to this career-relevant moment.",
  situation:
    "A situation is a career-relevant moment that anchors confirmed thoughts.",
  situationLong:
    "A situation is a career-relevant moment that anchors confirmed thoughts: a role, project, course, transition, achievement, challenge, or life experience.",
  anchorToSituation:
    "Choose where this thought belongs so it stays grounded in a real moment from your life.",
  addManually:
    "Write confirmed thoughts directly when you already know what you want to capture.",
  reviewSuggestions:
    "Confirm useful suggestions, edit what needs sharpening, and discard the rest.",
  profileFocus:
    "Use a focus lens to bring the most relevant confirmed thoughts forward for a profile reader.",
  profileFocusLong:
    "A focus lens represents what a profile reader may expect or want to find. Paste a job post, role brief, program description, or opportunity notes.",
  tailorToFocus:
    "Use a job post, role brief, or opportunity notes to order selected thoughts by reader relevance.",
  needsAnchorMode: "Anchor standalone thoughts to a situation.",
  anchorNeedsAnchorThoughts:
    "Choose a situation to anchor these confirmed thoughts in a real career moment.",
  anchorAndConfirmThought:
    "Choose a situation to anchor this suggested thought before confirming it into your profile.",
  deletedSituationThoughtsNeedAnchor:
    "Confirmed thoughts will stay in your profile with their situation anchor removed.",
  situationDeletedThoughtsNeedAnchor:
    "Situation deleted. Confirmed thoughts kept their profile place with anchors removed.",
  situationsDeletedThoughtsNeedAnchor:
    "Situations deleted. Confirmed thoughts kept their profile place with anchors removed.",
  needsAnchorDownloadBlocked:
    "Finish Needs anchor before preparing a profile export.",
  needsAnchorOrderBlocked:
    "Finish Needs anchor before ordering selected profile material.",
  allThoughtsAnchored:
    "No confirmed thoughts need a situation anchor right now.",
  noMatchingNeedsAnchor:
    "Adjust search filters or switch perspective if you expected more.",
  searchMode: "Select profile items to prepare a profile version.",
  orderMode: "Fine-tune the order manually or use a focus lens.",
} as const;

export const PROFILE_SHAPING_STUDIO_GUIDANCE = {
  manualAdd: {
    title: "Shape a situation",
    message:
      "Choose a career situation, open Shape this situation, then choose Add manually.",
    compactMessage: "Choose situation, then Add manually.",
  },
  reviewSuggestions: {
    title: "Review suggestions",
    message: "Confirm useful suggestions with the check icon on each thought.",
    compactMessage: "Tap check icon or swipe to confirm.",
  },
  loadedSelectionSearch: {
    title: "Profile version loaded",
    message:
      "You're viewing a saved profile version. Adjust filters or load more results to reveal other matching profile material.",
    compactMessage:
      "Profile version loaded. Filters and loaded pages affect what else is visible.",
  },
} as const;

export const PROFILE_SHAPING_EMPTY_STATES = {
  backlogSource: {
    title: "No source material yet.",
    message:
      "Add source material to Backlog so it stays available as evidence and can be shaped into suggested thoughts.",
  },
  situation: {
    title: "Start shaping this situation.",
    message:
      "Use Shape this situation to add thoughts directly or start from source material that reflects this career-relevant moment.",
  },
  suggestions: {
    title: "No suggestions yet.",
    message:
      "Start from source to generate suggested thoughts, then review what belongs in your profile.",
  },
  confirmedThoughts: {
    title: "No confirmed thoughts yet.",
    message:
      "Confirm suggestions or add thoughts manually to build this part of your profile.",
  },
  orderMode: {
    title: "No thoughts selected for ordering.",
    message:
      "Return to Search mode, select confirmed thoughts, then order the profile export.",
  },
  profileFocus: {
    title: "No focus lens yet.",
    message:
      "Add a focus lens from a job post, role brief, or opportunity description to order selected thoughts by reader relevance.",
  },
  needsAnchor: {
    title: PROFILE_SHAPING_LABELS.allThoughtsAnchored,
    message: PROFILE_SHAPING_MESSAGES.allThoughtsAnchored,
  },
  needsAnchorSearch: {
    title: "No matching thoughts need anchor",
    message: PROFILE_SHAPING_MESSAGES.noMatchingNeedsAnchor,
  },
} as const;

export const PROFILE_SHAPING_LOADING_STATES = {
  extraction: {
    loadingMessage: "Shaping suggestions.",
    message:
      "Reading the source material and preparing suggestions for review.",
    actionLabel: "Shaping",
  },
  suggestions: {
    loadingMessage: "Loading suggestions.",
    message: "Preparing suggested thoughts for review.",
  },
  confirmation: {
    loadingMessage: "Confirming thoughts.",
    message: "Saving selected thoughts into your profile.",
    actionLabel: "Confirming",
  },
  manualAdd: {
    loadingMessage: "Adding thoughts.",
    message: "Saving confirmed thoughts into this career situation.",
    actionLabel: "Adding thoughts",
  },
  ordering: {
    loadingMessage: "Preparing order mode.",
    message: "Loading selected confirmed thoughts for ordering.",
  },
} as const;

export type ProfileShapingActionId = "source" | "manual" | "review";
export type ProfileShapingLauncherActionId =
  | ProfileShapingActionId
  | "focus"
  | "needs_anchor"
  | "decompose_cv";

export type ProfileShapingAction = {
  id: ProfileShapingActionId;
  label: string;
  description: string;
  icon: GenericIconType;
};

export type ProfileShapingLauncherAction = Omit<ProfileShapingAction, "id"> & {
  id: ProfileShapingLauncherActionId;
  humanReadable: string;
};

export const PROFILE_SHAPING_ACTIONS = [
  {
    id: "source",
    label: PROFILE_SHAPING_LABELS.extractionPhase,
    description:
      "Create suggested thoughts from notes, CVs, transcripts, or rough drafts.",
    icon: "extract",
  },
  {
    id: "manual",
    label: PROFILE_SHAPING_LABELS.manualPhase,
    description:
      "Write confirmed thoughts directly when you already know what you want to capture.",
    icon: "add",
  },
  {
    id: "review",
    label: PROFILE_SHAPING_LABELS.reviewPhase,
    description:
      "Confirm, edit, or remove generated thoughts before they become profile content.",
    icon: "suggested_thought_review",
  },
] as const satisfies readonly ProfileShapingAction[];

export const PROFILE_SHAPING_LAUNCHER_ACTIONS = [
  {
    id: "source",
    label: PROFILE_SHAPING_LABELS.addSourceMaterial,
    description:
      "Store notes, CV text, transcripts, or rough stories in Backlog as source material.",
    humanReadable: "Add source material to Backlog",
    icon: "origin_source",
  },
  {
    id: "focus",
    label: PROFILE_SHAPING_LABELS.addFocus,
    description:
      "Save a job post, role brief, or opportunity notes as a focus lens for reader-relevance ordering.",
    humanReadable:
      "Add a focus lens to order selected thoughts for a profile reader",
    icon: "profile_focus",
  },
  {
    id: "needs_anchor",
    label: PROFILE_SHAPING_LABELS.reviewNeedsAnchor,
    description:
      "Review thoughts that need a situation anchor before they are ready for profile exports.",
    humanReadable: "Review thoughts that need a situation anchor",
    icon: "anchor",
  },
  {
    id: "review",
    label: PROFILE_SHAPING_LABELS.reviewPhase,
    description:
      "Open Studio to review suggested thoughts and confirm what belongs in your profile.",
    humanReadable: "Open Studio to review suggested thoughts",
    icon: "suggested_thought_review",
  },
  {
    id: "decompose_cv",
    label: "Decompose CV",
    description:
      "Upload a CV and decompose it into source material for profile shaping.",
    humanReadable: "Upload and decompose a CV for profile shaping",
    icon: "upload",
  },
] as const satisfies readonly ProfileShapingLauncherAction[];
